import Phaser from 'phaser';
import { haptics } from '../../platform/haptics';
import { deleteOnlineData, ensurePlayer, onlineOn, setOnlineScores } from '../online';
import { saves } from '../records';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { label, UI } from '../ui/text';
import { wrapLineCount } from '../ui/wrap';
import { wipeTo } from './WipeScene';
import { setShowOnline } from '../hub';
import { friendAlertsAvailable, setFriendAlerts, testFriendAlert, type PushChange } from '../push';
import { pushDenied } from '../../platform/push';

type Status = 'joining' | 'offline' | 'deleting' | 'deleted';

const STATUS_TEXT: Record<Status, { text: string; color: number }> = {
  joining: { text: 'Setting up...', color: UI.dim },
  offline: { text: "Can't reach the server right now. Try again later.", color: UI.dim },
  deleting: { text: 'Deleting...', color: UI.dim },
  deleted: { text: 'Your online data is deleted. Online scores are off.', color: UI.good },
};

/**
 * Settings > ONLINE (docs/ONLINE.md, "Players and names"): the privacy side of online play. Online
 * scores on or off, blocked players, and DELETE MY ONLINE DATA, which Apple requires even for
 * accounts the game made by itself. The name is picked in CLUB > YOU (NameScene).
 */
interface Note {
  text: string;
  color: number;
}

/** What Alerts says under its switch after a change that didn't go through. */
function alertNote(result: PushChange): Note | null {
  if (result === 'on' || result === 'off') return null;
  if (result === 'blocked') {
    return {
      text: "Allow lawnmower's notifications in your phone's Settings first.",
      color: UI.danger,
    };
  }
  if (result === 'offline') return { text: "Can't reach the server right now.", color: UI.danger };
  // Apple's own words, shortened, so a report from a phone says what went wrong.
  const why = result.noToken.length > 60 ? `${result.noToken.slice(0, 57)}...` : result.noToken;
  return { text: `Your phone couldn't sign up for alerts. (${why})`, color: UI.danger };
}

export class OnlineScene extends Phaser.Scene {
  private status: Status | null = null;
  private alertNote: Note | null = null;
  /** Notifications are off for the app in the phone's Settings: the switch shows OFF. */
  private alertsDenied = false;
  private confirmingDelete = false;
  private busy = false;

  constructor() {
    super('Online');
  }

  init(): void {
    this.alertNote = null;
    this.alertsDenied = false;
    this.status = null;
    this.confirmingDelete = false;
    this.busy = false;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    void this.join();
    if (friendAlertsAvailable()) {
      void pushDenied().then((denied) => {
        this.alertsDenied = denied;
        if (denied) this.redraw();
      });
    }
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    clearScene(this);
    this.draw();
  };

  /** With online scores on and no player yet, joins (for BLOCKED). */
  private async join(): Promise<void> {
    if (!onlineOn() || saves().save.online.player) return;
    this.status = 'joining';
    this.redraw();
    const player = await ensurePlayer();
    this.status = player ? null : 'offline';
    this.redraw();
  }

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    const g = this.add.graphics();
    g.fillStyle(UI.panel).fillRect(0, 0, width, 22);
    g.fillStyle(UI.rule).fillRect(0, 22, width, 1);
    label(this, 6, 4, 'ONLINE', 2);
    if (this.confirmingDelete) {
      this.drawConfirm();
      return;
    }
    const on = saves().save.settings.onlineScores;
    const textW = width - 28;

    // Online scores, on or off.
    const note = on
      ? 'Your best Daily times and Endless scores go on the leaderboards under your name (pick it in CLUB > YOU).'
      : 'Off: nothing is sent. Turn it on to join the leaderboards.';
    const noteH = wrapLineCount(note, textW) * 10;
    navyPanel(g, 4, 28, width - 8, 30 + noteH);
    label(this, 12, 38, 'Online scores');
    bevelButton(this, width - 68, 34, 56, 16, on ? 'ON' : 'OFF', () => this.toggle(), on);
    label(this, 12, 54, note, 1, UI.dim).setMaxWidth(textW);
    let y = 28 + 30 + noteH + 10;
    // Friends see a dot while this player has the game open (docs/FRIENDS.md).
    if (on) {
      const shown = saves().save.settings.showOnline;
      navyPanel(g, 4, y - 4, width - 8, 34);
      label(this, 12, y + 2, 'Show me online');
      bevelButton(
        this,
        width - 68,
        y - 2,
        56,
        16,
        shown ? 'ON' : 'OFF',
        () => {
          setShowOnline(!shown);
          this.redraw();
        },
        shown,
      ).setData('label', 'SHOW ONLINE');
      label(this, 12, y + 17, 'Friends see when you play.', 1, UI.dim);
      y += 40;
    }
    // Alerts (docs/FRIENDS.md, step 4): push notifications while the app is closed. (Called Friend
    // alerts until the Daily's lead joined them, 2026-10-29.)
    if (on && friendAlertsAvailable()) {
      const alerts = saves().save.settings.friendAlerts && !this.alertsDenied;
      const note = this.alertNote;
      const text = note?.text ?? 'Invites, requests, a friend in your room, your Daily lead taken.';
      const noteH = wrapLineCount(text, textW) * 10;
      navyPanel(g, 4, y - 4, width - 8, 24 + noteH + (alerts ? 22 : 0));
      label(this, 12, y + 2, 'Alerts');
      bevelButton(
        this,
        width - 68,
        y - 2,
        56,
        16,
        alerts ? 'ON' : 'OFF',
        () => void this.changeAlerts(!alerts),
        alerts,
      ).setData('label', 'ALERTS');
      label(this, 12, y + 17, text, 1, note?.color ?? UI.dim).setMaxWidth(textW);
      if (alerts) {
        bevelButton(this, 12, y + 20 + noteH, 80, 16, 'SEND A TEST', () => void this.test());
      }
      y += 30 + noteH + (alerts ? 22 : 0);
    }

    if (this.status) {
      const s = STATUS_TEXT[this.status];
      label(this, width / 2, y, s.text, 1, s.color)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
    }

    const backY = height - 30;
    if (on && saves().save.online.player) {
      bevelButton(this, 6, backY - 52, width - 12, 20, 'BLOCKED', () => wipeTo(this, 'Blocked'));
    }
    if (saves().save.online.player || on) {
      bevelButton(this, 6, backY - 28, width - 12, 20, 'DELETE MY ONLINE DATA', () => {
        this.confirmingDelete = true;
        this.redraw();
      });
    }
    bevelButton(this, 6, backY, width - 12, 24, 'BACK', () => wipeTo(this, 'Settings'));
  }

  /** The delete step: what goes, that it can't be undone, DELETE or KEEP. */
  private drawConfirm(): void {
    const { width, height } = this.scale.gameSize;
    const textW = width - 28;
    const lines = [
      { text: 'Delete your name, scores and runs from the leaderboards?', color: UI.accent },
      { text: "This can't be undone. Online scores turn off.", color: UI.text },
      { text: 'Your lawns, coins and mowers on this device stay.', color: UI.dim },
    ];
    const g = this.add.graphics();
    let y = 34;
    const h = lines.reduce((t, l) => t + wrapLineCount(l.text, textW) * 10 + 4, 0);
    navyPanel(g, 4, y - 6, width - 8, h + 8);
    for (const l of lines) {
      label(this, 14, y, l.text, 1, l.color).setMaxWidth(textW);
      y += wrapLineCount(l.text, textW) * 10 + 4;
    }
    if (this.status === 'offline' || this.status === 'deleting') {
      const s = STATUS_TEXT[this.status];
      label(this, width / 2, y + 10, s.text, 1, s.color)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
    }
    const backY = height - 30;
    bevelButton(this, 6, backY - 34, width - 12, 28, 'DELETE', () => void this.remove(), true, 2);
    bevelButton(this, 6, backY, width - 12, 24, 'KEEP', () => {
      this.confirmingDelete = false;
      this.status = null;
      this.redraw();
    });
  }

  private async changeAlerts(on: boolean): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    haptics.tap();
    // Asking the phone and Apple can take a few seconds: say so at once.
    if (on) {
      this.alertNote = { text: 'Turning on...', color: UI.dim };
      this.redraw();
    }
    const result = await setFriendAlerts(on);
    this.busy = false;
    this.alertsDenied = result === 'blocked';
    this.alertNote = alertNote(result);
    this.redraw();
  }

  private async test(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.alertNote = { text: 'Sending...', color: UI.dim };
    this.redraw();
    const answer = await testFriendAlert();
    this.busy = false;
    this.alertNote = answer?.sent
      ? { text: 'Sent! It should show in a moment.', color: UI.good }
      : answer
        ? {
            text: `The test didn't go through (${answer.reason ?? answer.status}).`,
            color: UI.danger,
          }
        : { text: "Can't reach the server right now.", color: UI.danger };
    this.redraw();
  }

  private toggle(): void {
    if (this.busy) return;
    const on = !saves().save.settings.onlineScores;
    // Online off: friend alerts stop too.
    if (!on && saves().save.settings.friendAlerts) void setFriendAlerts(false);
    setOnlineScores(on);
    haptics.tap();
    this.status = null;
    this.redraw();
    if (on) void this.join();
  }

  private async remove(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.status = 'deleting';
    this.redraw();
    const ok = await deleteOnlineData();
    this.busy = false;
    if (ok) {
      this.confirmingDelete = false;
      this.status = 'deleted';
      haptics.impact();
    } else {
      this.status = 'offline';
    }
    this.redraw();
  }
}
